package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.coroutines.delay
import org.example.project.service.CustomerApi
import org.example.project.service.FeedControlApi
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.LoginUserApi
import org.example.project.service.SnapApi
import org.example.project.service.SnapPopupService
import org.example.project.utils.All

@Composable
internal fun DashboardRuntimeController(
    art: String,
    selectedKey: String,
    layoutSettingsKey: String,
    sessionConfigVersion: Int,
    userSettingsVersion: Long,
    snapRequestVersion: Long,
    monitorAvailable: Boolean,
    monitorEnabled: Boolean,
    activeFirmenId: String,
    forceContentScreenKey: String?,
    currentLoginUserId: String?,
    openTopbarScreens: List<String>,
    onCurrentLoginUserChange: (All?) -> Unit,
    onCurrentCompanyChange: (All?) -> Unit,
    onTopbarAutoHideChange: (Boolean) -> Unit,
    onSidebarAutoHideChange: (Boolean) -> Unit,
    onSidebarCollapsedChange: (Boolean) -> Unit,
    onMonitorEnabledPreferenceChange: (Boolean) -> Unit,
    onUserFeedPushEnabledChange: (Map<String, Boolean>) -> Unit,
    onTopbarVisibilityChange: (Boolean) -> Unit,
    onSidebarVisibilityChange: (Boolean) -> Unit,
    onDashboardFeedConfigChange: (Map<String, Map<String, String>>) -> Unit,
    onDashboardMonitorChange: (All) -> Unit,
    onOpenTopbarScreensChange: (List<String>) -> Unit,
    onLayoutPreferencesReady: () -> Unit,
    userFeedVisible: Map<String, Boolean>
) {
    val monitorLiveVersion = GlobalManager.sessionConfigVersion()
    val feedConfigVersion = FeedControlApi.version()

    LaunchedEffect(selectedKey, sessionConfigVersion) {
        val userId = LoginService.getId()
        val freshUser = LoginUserApi.loadUser(userId)
        if (freshUser != null) {
            LoginService.updateUser { freshUser }
            onCurrentLoginUserChange(freshUser)
        } else {
            onCurrentLoginUserChange(LoginService.getUser())
        }

        val firmenId = LoginService.getUser()?.firmenId.orEmpty().trim().ifBlank { GlobalManager.currentFirmenId.trim() }
        val loadedCompany = if (firmenId.isBlank()) null else CustomerApi.loadDetails(firmenId)
        val sessionCompany = LoginService.getUser()?.let { user ->
            val companyName = user.firma.trim().ifBlank { user.kunde.trim() }
            if (firmenId.isBlank() || companyName.isBlank()) null else All(
                id = firmenId,
                firmenId = firmenId,
                firma = companyName,
                kunde = companyName
            )
        }
        onCurrentCompanyChange(loadedCompany ?: sessionCompany)
    }

    LaunchedEffect(layoutSettingsKey, userSettingsVersion) {
        val preferences = loadDashboardLayoutPreferences(
            settingsKey = layoutSettingsKey,
            forceReload = userSettingsVersion > 0L
        )
        onTopbarAutoHideChange(preferences.topbarAutoHide)
        onSidebarAutoHideChange(preferences.sidebarAutoHide)
        onSidebarCollapsedChange(preferences.sidebarCollapsed)
        onMonitorEnabledPreferenceChange(preferences.monitorEnabled)
        onUserFeedPushEnabledChange(preferences.feedPushEnabled)
        onTopbarVisibilityChange(!preferences.topbarAutoHide)
        onSidebarVisibilityChange(!preferences.sidebarAutoHide)
        onLayoutPreferencesReady()
    }

    LaunchedEffect(art, sessionConfigVersion, feedConfigVersion) {
        onDashboardFeedConfigChange(
            runCatching { FeedControlApi.loadFeedConfig() }
                .getOrDefault(DashboardFeedRegistry.defaultConfig())
        )
    }

    LaunchedEffect(art, monitorEnabled, monitorAvailable, monitorLiveVersion, userFeedVisible) {
        if (!monitorAvailable || !monitorEnabled) {
            onDashboardMonitorChange(emptyDashboardMonitor())
            return@LaunchedEffect
        }

        while (true) {
            onDashboardMonitorChange(
                runCatching { loadDashboardMonitorSnapshot(art, userFeedVisible) }.getOrDefault(emptyDashboardMonitor())
            )
            delay(5000)
        }
    }

    LaunchedEffect(art, activeFirmenId, currentLoginUserId, snapRequestVersion) {
        val userId = LoginService.getId().trim()
        val resolvedFirmenId = activeFirmenId.trim()
        if (userId.isBlank()) {
            SnapPopupService.replacePopups(art, emptyList())
            return@LaunchedEffect
        }
        val pending = runCatching {
            SnapApi.loadPendingRequests(
                userId = userId,
                firmenId = resolvedFirmenId
            )
        }.getOrDefault(emptyList())
        SnapPopupService.replacePopups(art, pending)
    }

    LaunchedEffect(forceContentScreenKey) {
        val forcedKey = forceContentScreenKey?.takeIf { it.isNotBlank() } ?: return@LaunchedEffect
        if (forcedKey in openTopbarScreens || openTopbarScreens.isNotEmpty()) {
            onOpenTopbarScreensChange(emptyList())
        }
    }
}
